<template>
  <div>
    <div class="row">
      <div class="col-sm-12">
        <h1 class="page-title pull-left">VDCI Site &amp; Activity Map</h1>
      </div>
    </div>

    <div class="panel" v-if="!loading">
      <div class="main-container">
        <div class="flex">
          <google-map
            v-if="google"
            :onFirstLoad="onMapFirstLoad"
            :google="google"
            :zoom="10"
            :selectedZone="selectedZone"
            :showFilterInfo="true"
            :draggable="true"
            :height="773"
            :topCenterButtonSettings="getTopCenterButtonSettings"
            :onBoundsChanged="onBoundsChanged"
            :showRequestLimitError="displayingRequestLimitError"
            :showRenderLimitError="displayingRenderLimitError"
          >
            <template slot-scope="{ google, map, provideBound, fitBound, mapBounds }">
              <div v-for="(mappable, i) in visibleMappables" :key="mappable.id">
                <keep-alive>
                  <map-marker
                    :mappable="mappable"
                    :google="google"
                    :map="map"
                    :key="mappable.id"
                    :fade="shouldFade(mappable)"
                    :provideBound="provideBound"
                    :fitBound="fitBound"
                    :mapBounds="mapBounds"
                    :index="i + 1"
                  />
                </keep-alive>
              </div>
            </template>
          </google-map>

          <sidebar :viewingFilters="showFilters" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loadGoogle, { getGeocodeAddress } from '../../utils/google-maps';
import SiteService from '../../services/v2/SiteService';
import NoSpraysService from '../../services/v2/NoSpraysService';
import ServiceRequestsService from '../../services/v2/ServiceRequestsService';
import ZonesService from '../../services/ZonesService';
import Sidebar from './sidebar';
import GoogleMap from './google-map';
import MapMarker from './map-marker';
import { mapGetters } from 'vuex';
import Site from '../../store/models/site';
import NoSpray from '../../store/models/no-spray';
import ServiceRequest from '../../store/models/service-request';
import loader from '../../utils/loader';
import getProperCoordinateType from '../../utils/getProperCoordinateType';

export default {
  name: 'site-and-activity-map',

  components: {
    Sidebar,
    GoogleMap,
    MapMarker,
  },

  data() {
    return {
      loading: false,
      google: window.google,
      mapHasMoved: true,
      displayingRequestLimitError: false,
    };
  },

  computed: {
    ...mapGetters(['visibleMappables', 'selectedZone']),
    shouldFade() {
      const { selectedMappable } = this.$store.state.map;
      return selectedMappable === null
        ? () => false
        : (mappable) => mappable.id != selectedMappable.id;
    },
    showFilters() {
      return this.$store.state.map.settings.displayFilterSettings;
    },
    getTopCenterButtonSettings() {
      return {
        text: 'Load Sites in this Area',
        onClick: this.onLoadSitesClicked,
        visible: this.mapHasMoved,
      };
    },
    displayingRenderLimitError() {
      const { activeMarkers } = this.$store.state.map;
      return activeMarkers >= 5000;
    },
  },

  methods: {
    onMapFirstLoad(map) {
      const { $store } = this;

      const afterLoadingSites = () => loader.lookups().then(() => {
        if ($store.state.map.zones.length == 1) {
          ZonesService.getList().then(({ data }) => {
            const zones = data.map(({ id, attributes }) => {
              const { coordinates, name } = attributes;
              const points = coordinates.map(({ lat, lon }) => ({
                lat,
                lng: lon,
              }));
              return {
                id,
                name,
                points,
              };
            });
            $store.commit('setMapZones', zones);
            $store.commit('setZonesForSprayPaths', zones);
          });
        }
      });

      this.onLoadSitesClicked(map, afterLoadingSites);
    },


    onLoadSitesClicked(map, callback) {
      this.displayingRequestLimitError = false;
      const { $store } = this;
      $store.commit('setLoadingMoreMappables', true);
      const $this = this;
      this.mapHasMoved = false;
      const bounds = map.getBounds();
      const boundsNE = bounds.getNorthEast();
      const boundsSW = bounds.getSouthWest();
      const ne = {
        lat: boundsNE.lat(),
        lng: boundsNE.lng(),
      };
      const sw = {
        lat: boundsSW.lat(),
        lng: boundsSW.lng(),
      };

      const minDistance = 0.1;
      const minDistanceHalved = minDistance * 0.5;

      const latDiff = Math.abs(ne.lat - sw.lat);
      if(latDiff < minDistance) {
        const latAvg = (ne.lat + sw.lat) * 0.5;
        ne.lat = latAvg + minDistanceHalved;
        sw.lat = latAvg - minDistanceHalved;
      }
      
      const lngDiff = Math.abs(ne.lng - sw.lng);
      if(lngDiff < minDistance) {
        const lngAvg = (ne.lng + sw.lng) * 0.5;
        ne.lng = lngAvg + minDistanceHalved;
        sw.lng = lngAvg - minDistanceHalved;
      }
      
      const viewBounds = { ne, sw };
      return SiteService.getSitesInArea(viewBounds).then(sitesRes => {
        NoSpraysService.getNoSpraysInArea(viewBounds).then(noSpraysRes => {
          ServiceRequestsService.getServiceRequestsInArea(viewBounds).then(serviceRequestsRes => {
            const sites = sitesRes;
            const noSprays = noSpraysRes;
            const serviceRequests = serviceRequestsRes;
            const outstandingServiceRequests = serviceRequests.filter(
              ({ status }) => status === 'Outstanding'
            );

            var newMappables = [];
            newMappables.push.apply(
              newMappables,
              sites.map((site) => new Site(site))
            );
            newMappables.push.apply(
              newMappables,
              noSprays.map((noSpray) => new NoSpray(noSpray))
            );
            newMappables.push.apply(
              newMappables,
              outstandingServiceRequests.map(
                (outstandingServiceRequest) =>
                  new ServiceRequest(outstandingServiceRequest)
              )
            );

            newMappables = newMappables.filter((mappable) => {
              const properCoordinates = getProperCoordinateType(mappable);
              if (properCoordinates === false) {
                console.warn(
                  'unable to map object with invalid coordinates: ',
                  mappable
                );
                return false;
              }
              mappable.coordinates = properCoordinates;
              return true;
            });

            $store.commit('updateSites', newMappables);

            $store.commit('handleChangeFilters', {
              ...$store.state.map.settings.filters,
            });
            $store.commit('setLoadingMoreMappables', false);
            
            if(callback) {
              callback();
            }
          })
          .catch(err => {
            this.displayingRequestLimitError = true;
            $store.commit('setLoadingMoreMappables', false);
          })
        })
        .catch(err => {
          this.displayingRequestLimitError = true;
            $store.commit('setLoadingMoreMappables', false);
        })
      })
      .catch(err => {
        this.displayingRequestLimitError = true;
        $store.commit('setLoadingMoreMappables', false);
      })
    },
    onBoundsChanged() {
      this.mapHasMoved = true;
    }
  },

  mounted() {
    const { $store } = this;
    const $this = this;
    const onLoad = async function() {
      $this.google = window.google;
      const activeFilter = $store.state.map.settings.filters.sortOrder.by;
      if ($store.state.map.mappables.length == 0) {
        $store.commit('init');
      }
    };

    loadGoogle(onLoad);
  },
};
</script>

<style lang="scss">
.flex {
  display: flex;
}

.main-container {
  height: fit-content;
  padding: 8px;
}
</style>
